import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import axios from 'axios';

interface User {
  id: string;
  userId: string;
  role: string;
  fullName?: string; // Add fullName property
  hasProvidedAdditionalInfo?: boolean;
  isSocialUser?: boolean;
  todayCounselingCount: number;
  referredCounselingCount: number;
  isPaidUser: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  setUser: (user: User | null) => void;
  hasSubmittedToday: boolean; // Add this state
  login: (userData: User, token: string) => Promise<void>;
  loading: boolean;
  logout: () => void;
  checkSubmissionStatus: () => Promise<boolean>; // Add this function
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// JWT 토큰 디코딩 함수
const decodeToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// 토큰 만료 체크 함수
const isTokenExpired = (token: string) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [hasSubmittedToday, setHasSubmittedToday] = useState<boolean>(true); // Default to true to avoid blocking on load
  const [loading, setLoading] = useState<boolean>(true); // ✅ 추가

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      try {
        if (token && userData) {
          if (isTokenExpired(token)) {
            logout(false); // ✅ redirect 없이 상태만 정리
            return;
          }

          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsAuthenticated(true);

          await checkSubmissionStatus(token);
        }
      } catch (e) {
        logout(false);
      } finally {
        setLoading(false); // ✅ 무조건 실행
      }
    };

    init();
  }, []);

  // 주기적 토큰 만료 체크
  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem('token');
      if (token && isTokenExpired(token)) {
        logout();
      }
    };

    // 1분마다 토큰 만료 체크
    const interval = setInterval(checkTokenExpiry, 60000);

    return () => clearInterval(interval);
  }, []);

  const checkSubmissionStatus = async (
    token: string | null = localStorage.getItem('token')
  ): Promise<boolean> => {
    if (!token) return true;
    try {
      const response = await axios.get('/api/daily-journals/today', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHasSubmittedToday(response.data.hasSubmittedToday);
      return response.data.hasSubmittedToday;
    } catch (error) {
      console.error('Failed to check daily journal status.', error);
      setHasSubmittedToday(true); // Default to true on error to avoid blocking user
      return true;
    }
  };

  const login = async (userData: User, token: string): Promise<void> => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    await checkSubmissionStatus(token);
  };

  const logout = (redirect: boolean = true) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setHasSubmittedToday(true);
    setLoading(false); // ✅ 어떤 경우든 loading 해제

    if (redirect) {
      window.location.replace('/login');
    }
  };

  const value = {
    isAuthenticated,
    user,
    setUser,
    hasSubmittedToday,
    login,
    loading,
    logout,
    checkSubmissionStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
